// Archive calendar: a month grid of past daily words, each cell showing the
// player's own state (not started / in progress / solved + attempts) and, on
// tap, opening that day to replay. Never shows the word (ARCHIVE_DESIGN §7).
//
// Purely presentational + month navigation: it takes the days from GET /archive
// as a prop and reports a picked date up. Bounds prev/next to the range actually
// present, so there is no way to page into empty months or the future.
//
// The month can be LIFTED by the parent (`month` + `onMonthChange`). Opening a day
// unmounts this component, so month state kept only in here was lost on the way
// back — page into January, play a word, return, and the calendar was on the
// current month again. A player working through an old month re-navigated there
// for every single word (reported 2026-08-29). Uncontrolled use still works: with
// no `month` prop it keeps its own, exactly as before.
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChestIcon, CloudOffIcon, LockIcon, PlayIcon, TrophyIcon } from "./icons";

// Monday-first weekday order (cs/sk/pl/hr/de all start the week on Monday).
const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const ym = (dateStr) => dateStr.slice(0, 7); // "2026-07-15" -> "2026-07"

// Days with no word because the game was down (Google suspended the project from
// 9. 10. 2026 afternoon to 12. 10. ~15:45; the Friday word stayed on until the
// next rotation and is archived under 12. 10.). Without a mark they read as a
// bug — a hole in the run of days. Not playable, just explained.
export const VYPADKOVE_DNY = new Set(["2026-10-09", "2026-10-10", "2026-10-11"]);

// Which grid column (0=Mon .. 6=Sun) a given Y-M-D falls on, without timezone
// surprises: build the date at UTC noon so no local offset can shift the day.
function weekdayIndex(y, m, d) {
  const js = new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay(); // 0=Sun..6=Sat
  return (js + 6) % 7; // -> 0=Mon..6=Sun
}

function daysInMonth(y, m) {
  return new Date(Date.UTC(y, m, 0, 12)).getUTCDate();
}

export function statusClass(status) {
  if (status === "guessed") return "archive-day--solved";
  if (status === "in_progress") return "archive-day--progress";
  return "archive-day--todo";
}

export default function ArchiveCalendar({
  days,
  loading,
  onPickDay,
  maskWord = (w) => w,
  todayIso = null,
  onPickToday = null,
  month = null,
  onMonthChange = null,
}) {
  const { t } = useTranslation();

  // Index days by date and collect the months that actually have entries.
  const { byDate, months } = useMemo(() => {
    const map = {};
    const monthSet = new Set();
    for (const d of days || []) {
      map[d.date] = d;
      monthSet.add(ym(d.date));
    }
    return { byDate: map, months: [...monthSet].sort() }; // ascending
  }, [days]);

  // Default to TODAY's month when it has entries, else the most recent present.
  // Derived (not a one-shot initial state) so it's right even if `days` load
  // after mount — the calendar always opens on today, not on whatever month the
  // initial (possibly empty) data happened to leave selected.
  const defaultIdx = useMemo(() => {
    if (!months.length) return 0;
    const now = new Date();
    const todayYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const i = months.indexOf(todayYm);
    return i !== -1 ? i : months.length - 1;
  }, [months]);

  // null = follow the default (today); a number = the user navigated there.
  const [monthIdx, setMonthIdx] = useState(null);
  // The parent's remembered month wins when it still exists in the data. It is
  // kept as a "YYYY-MM" STRING rather than an index on purpose: indices shift as
  // new months appear at the end, so a remembered index would silently start
  // pointing at a different month. An unknown month falls back to the default.
  const fromParent = month ? months.indexOf(month) : -1;
  const chosen = fromParent !== -1 ? fromParent : monthIdx == null ? defaultIdx : monthIdx;
  const safeIdx = Math.min(Math.max(0, chosen), Math.max(0, months.length - 1));
  const currentMonth = months[safeIdx];

  const grid = useMemo(() => {
    if (!currentMonth) return [];
    const [y, m] = currentMonth.split("-").map(Number);
    const total = daysInMonth(y, m);
    const lead = weekdayIndex(y, m, 1); // blank cells before the 1st
    const cells = Array.from({ length: lead }, () => null);
    for (let d = 1; d <= total; d++) {
      const iso = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push({ d, iso, day: byDate[iso] || null });
    }
    return cells;
  }, [currentMonth, byDate]);

  // Direction of the last month change — drives the slide-in animation so the
  // swipe has visible feedback (new month enters from the side you swiped from).
  const [slide, setSlide] = useState(null);
  const goToMonth = (idx) => {
    setMonthIdx(idx);
    if (onMonthChange) onMonthChange(months[idx]);
  };
  const goPrev = () => {
    setSlide("prev");
    goToMonth(Math.max(0, safeIdx - 1));
  };
  const goNext = () => {
    setSlide("next");
    goToMonth(Math.min(months.length - 1, safeIdx + 1));
  };

  // Horizontal swipe changes month; a tap (small dx) still opens a day, and a
  // vertical-dominant drag is left to scroll the grid.
  const touch = useRef(null);
  const onTouchStart = (e) => {
    const p = e.touches[0];
    touch.current = { x: p.clientX, y: p.clientY };
  };
  const onTouchEnd = (e) => {
    if (!touch.current) return;
    const p = e.changedTouches[0];
    const dx = p.clientX - touch.current.x;
    const dy = p.clientY - touch.current.y;
    touch.current = null;
    if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return; // not a horizontal swipe
    if (dx < 0) goNext(); // finger left → later month
    else goPrev(); // finger right → earlier month
  };

  if (loading) return <p className="profile-empty">{t("archive.loading")}</p>;
  if (!months.length) return <p className="profile-empty">{t("archive.empty")}</p>;

  const [y, m] = currentMonth.split("-").map(Number);
  const monthLabel = `${t(`archive.month.${m}`)} ${y}`;

  return (
    <div className="archive-calendar">
      <div className="archive-nav">
        <button
          className="archive-nav__btn"
          aria-label={t("archive.prevMonth")}
          disabled={safeIdx <= 0}
          onClick={goPrev}
        >
          ‹
        </button>
        <span className="archive-nav__label">{monthLabel}</span>
        <button
          className="archive-nav__btn"
          aria-label={t("archive.nextMonth")}
          disabled={safeIdx >= months.length - 1}
          onClick={goNext}
        >
          ›
        </button>
      </div>

      <div className="archive-weekdays">
        {WEEKDAY_KEYS.map((k) => (
          <div key={k} className="archive-grid__wd">
            {t(`archive.weekday.${k}`)}
          </div>
        ))}
      </div>

      {/* keyed on the month so React remounts it and the slide animation replays */}
      <div
        key={currentMonth}
        className={`archive-days${slide ? ` archive-days--${slide}` : ""}`}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {grid.map((cell, i) =>
          cell === null ? (
            <div key={`b${i}`} className="archive-day archive-day--blank" />
          ) : cell.day ? (
            <button
              key={cell.iso}
              className={`archive-day ${statusClass(cell.day.status)}${
                cell.day.status !== "not_started" ? " archive-day--played" : ""
              }${cell.day.locked ? " archive-day--locked" : ""}`}
              onClick={() => onPickDay(cell.iso)}
              aria-label={`${cell.d}. ${m}. ${y}${cell.day.locked ? ` · ${t("archive.locked")}` : ""}`}
              title={
                cell.day.locked
                  ? t("archive.locked")
                  : cell.day.win_rate != null
                    ? t("archive.difficulty", { rate: Math.round(cell.day.win_rate) })
                    : undefined
              }
            >
              {/* Date top-left (calendar convention). The icon + attempts block is
                  centred and identical across states, so those line up between a
                  solved and an unfinished day; the word is pinned to the bottom so
                  it can't shift them. */}
              <span className="archive-day__num">{cell.d}</span>
              <span className="archive-day__body">
                {/* The lock stands in for the chest on days I never played. A day I
                    already solved or started keeps its own icon — my own result is
                    mine to see, the lock only says I can't open it again. */}
                {cell.day.locked && cell.day.status === "not_started" ? (
                  <LockIcon size={18} className="archive-day__lock" />
                ) : cell.day.status === "guessed" ? (
                  <TrophyIcon size={22} />
                ) : cell.day.status === "in_progress" ? (
                  <PlayIcon size={22} />
                ) : (
                  <ChestIcon size={21} className="archive-day__chest" />
                )}
                {cell.day.status !== "not_started" && (
                  <span className="archive-day__attempts">{cell.day.attempts}</span>
                )}
              </span>
              {cell.day.status === "guessed" && cell.day.word && (
                <span className="archive-day__word" title={maskWord(cell.day.word)}>{maskWord(cell.day.word)}</span>
              )}
            </button>
          ) : cell.iso === todayIso && onPickToday ? (
            // Today is not an archive day (it's the live game), but leaving a hole
            // in the run of days read as "my started day vanished" in testing. Show
            // it, say what it is, and send the tap to the live board.
            <button
              key={cell.iso}
              className="archive-day archive-day--today"
              onClick={onPickToday}
              aria-label={`${cell.d}. ${m}. ${y} · ${t("archive.todayPlaying")}`}
              title={t("archive.todayPlaying")}
            >
              <span className="archive-day__num">{cell.d}</span>
              <span className="archive-day__body">
                <PlayIcon size={22} />
              </span>
              <span className="archive-day__today">{t("archive.todayShort")}</span>
            </button>
          ) : VYPADKOVE_DNY.has(cell.iso) ? (
            <div
              key={cell.iso}
              className="archive-day archive-day--outage"
              role="img"
              aria-label={`${cell.d}. ${m}. ${y} · ${t("archive.outage")}`}
              title={t("archive.outage")}
            >
              <span className="archive-day__num">{cell.d}</span>
              <span className="archive-day__body">
                <CloudOffIcon size={20} />
              </span>
              <span className="archive-day__outage">{t("archive.outageShort")}</span>
            </div>
          ) : (
            // a day inside the month but outside the catalog (e.g. the future)
            <div key={cell.iso} className="archive-day archive-day--blank">
              <span className="archive-day__num">{cell.d}</span>
            </div>
          )
        )}
      </div>
      {grid.some((cell) => cell && !cell.day && VYPADKOVE_DNY.has(cell.iso)) && (
        <p className="archive-outage-note">{t("archive.outageNote")}</p>
      )}
    </div>
  );
}
